import { z } from "zod/v4";
import type { AssetCameraKey, TenantId, SiteId } from "@visioni/a3s-schema";

import {
  AlarmActivatedActivityMetadata,
  AlarmRoutineConfiguredActivityMetadata,
  AlarmResponseConfiguredActivityMetadata,
  AlarmExceptionConfiguredActivityMetadata,
  AlarmTimezoneConfiguredActivityMetadata,
  AlarmSilenceConfiguredActivityMetadata,
  AlarmOverrideConfiguredActivityMetadata,
  AlarmContactsConfiguredActivityMetadata,
  SiteAlarmOverrideConfiguredActivityMetadata,
} from "./kinds/alarms";
import { UserPermissionGrantedActivityMetadata, UserPermissionRevokedActivityMetadata } from "./kinds/access";
import {
  LivestreamViewedActivityMetadata,
  ClipViewedActivityMetadata,
  ClipCompilationRequestedActivityMetadata,
  ClipCompilationViewedActivityMetadata,
} from "./kinds/media";
import { TestSignalTriggeredActivityMetadata } from "./kinds/monitoring";
import { DeterrenceTriggeredActivityMetadata, DeterrenceStoppedActivityMetadata } from "./kinds/deterrence";

export { ClipCompilationAccessMethod } from "./kinds/media";

export const ActivityKind = z.enum([
  "alarm_activated",
  "alarm_routine_configured",
  "alarm_response_configured",
  "alarm_exception_configured",
  "alarm_timezone_configured",
  "alarm_override_configured",
  "alarm_silence_configured",
  "alarm_contacts_configured",
  "site_alarm_override_configured",
  "user_permission_granted",
  "user_permission_revoked",
  "livestream_viewed",
  "clip_viewed",
  "clip_compilation_requested",
  "clip_compilation_viewed",
  "test_signal_triggered",
  "deterrence_triggered",
  "deterrence_stopped",
]);
export type ActivityKind = z.infer<typeof ActivityKind>;

/**
 * Kinds that only Visioni support staff may see.
 *
 * These record an internal operator action rather than something the tenant did
 * or had done to them, so surfacing them would expose how Visioni operates a
 * site. Compare {@link maskInternalActors} in the activity list read path, which
 * hides internal *identities* from tenants for the same reason.
 *
 * Enforced server-side in the list query — see `visibleActivityKinds`. Anything
 * added here is invisible to tenant users in both the returned rows and the
 * total count.
 */
export const SUPPORT_ONLY_ACTIVITY_KINDS: readonly ActivityKind[] = [
  "clip_compilation_requested",
  "test_signal_triggered",
];

/**
 * The activity kinds a caller is allowed to see.
 *
 * Support users see everything; everyone else sees everything except
 * {@link SUPPORT_ONLY_ACTIVITY_KINDS}.
 */
export function visibleActivityKinds(isSupportUser: boolean): ActivityKind[] {
  if (isSupportUser) return [...ActivityKind.options];
  return ActivityKind.options.filter((kind) => !SUPPORT_ONLY_ACTIVITY_KINDS.includes(kind));
}

export const ActivityEdgeEventState = z.enum(["uploading", "applying", "applied", "failed"]);
export type ActivityEdgeEventState = z.infer<typeof ActivityEdgeEventState>;

export const ActivityEdgeEvent = z.object({
  state: ActivityEdgeEventState,
  at: z.iso.datetime(),
  reason: z.string().nullable(),
});
export type ActivityEdgeEvent = z.infer<typeof ActivityEdgeEvent>;

export const ActivityActor = z.object({
  id: z.string().nullable(),
});
export type ActivityActor = z.infer<typeof ActivityActor>;

export const ActivityEdgeLifecycle = z.object({
  status: z.string(),
  statusUpdatedAt: z.iso.datetime(),
  statusReason: z.string().nullable(),
  correlationKey: z.string(),
  events: z.array(ActivityEdgeEvent),
});
export type ActivityEdgeLifecycle = z.infer<typeof ActivityEdgeLifecycle>;

export const ActivityMetadata = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("alarm_activated"), data: AlarmActivatedActivityMetadata }),
  z.object({ kind: z.literal("alarm_routine_configured"), data: AlarmRoutineConfiguredActivityMetadata }),
  z.object({ kind: z.literal("alarm_response_configured"), data: AlarmResponseConfiguredActivityMetadata }),
  z.object({ kind: z.literal("alarm_exception_configured"), data: AlarmExceptionConfiguredActivityMetadata }),
  z.object({ kind: z.literal("alarm_timezone_configured"), data: AlarmTimezoneConfiguredActivityMetadata }),
  z.object({ kind: z.literal("alarm_silence_configured"), data: AlarmSilenceConfiguredActivityMetadata }),
  z.object({ kind: z.literal("alarm_override_configured"), data: AlarmOverrideConfiguredActivityMetadata }),
  z.object({ kind: z.literal("alarm_contacts_configured"), data: AlarmContactsConfiguredActivityMetadata }),
  z.object({ kind: z.literal("site_alarm_override_configured"), data: SiteAlarmOverrideConfiguredActivityMetadata }),
  z.object({ kind: z.literal("user_permission_granted"), data: UserPermissionGrantedActivityMetadata }),
  z.object({ kind: z.literal("user_permission_revoked"), data: UserPermissionRevokedActivityMetadata }),
  z.object({ kind: z.literal("livestream_viewed"), data: LivestreamViewedActivityMetadata }),
  z.object({ kind: z.literal("clip_viewed"), data: ClipViewedActivityMetadata }),
  z.object({
    kind: z.literal("clip_compilation_requested"),
    data: ClipCompilationRequestedActivityMetadata,
  }),
  z.object({
    kind: z.literal("clip_compilation_viewed"),
    data: ClipCompilationViewedActivityMetadata,
  }),
  z.object({
    kind: z.literal("test_signal_triggered"),
    data: TestSignalTriggeredActivityMetadata,
  }),
  z.object({ kind: z.literal("deterrence_triggered"), data: DeterrenceTriggeredActivityMetadata }),
  z.object({ kind: z.literal("deterrence_stopped"), data: DeterrenceStoppedActivityMetadata }),
]);
export type ActivityMetadata = z.infer<typeof ActivityMetadata>;

export const Activity = z.object({
  id: z.string(),
  tenantId: z.custom<TenantId>(),
  siteId: z.custom<SiteId>().nullable(),
  cameraKey: z.custom<AssetCameraKey>().nullable(),
  kind: ActivityKind,
  summary: z.string(),
  metadata: ActivityMetadata,
  actor: ActivityActor,
  edge: ActivityEdgeLifecycle.nullable(),
  createdAt: z.iso.datetime(),
});
export type Activity = z.infer<typeof Activity>;

export const ActivityListResponse = z.object({
  count: z.number().int().nonnegative(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(Activity),
});
export type ActivityListResponse = z.infer<typeof ActivityListResponse>;

/**
 * Input for recording a new activity item. Callers construct the summary
 * and metadata — the recorder handles column mapping, timestamps, and INSERT.
 * The `kind` column is derived from `metadata.kind` to prevent mismatches.
 */
export type RecordActivityInput = {
  tenantId: TenantId;
  siteId?: string | null;
  assetId?: string | null;
  cameraId?: string | null;
  actorId?: string | null;
  summary: string;
  metadata: ActivityMetadata;
  status?: string | null;
  correlationKey?: string | null;
};
